import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Briefcase, GraduationCap, ArrowUpRight } from "lucide-react";
import { EXPERIENCE, TRAININGS } from "@/data/content";

export default function Journey() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const lineHeight = useTransform(scrollYProgress, [0.05, 0.95], ["0%", "100%"]);

  return (
    <section
      id="journey"
      ref={sectionRef}
      className="relative py-28 md:py-36 overflow-hidden"
      data-testid="journey-section"
    >
      <div className="section-x mx-auto max-w-[1400px]">
        <SectionHeader
          eyebrow="03 — Professional Summary"
          title={
            <>
              12 Years, <br className="hidden md:block" />
              <span className="text-ink/50">Two Parallel Journeys.</span>
            </>
          }
          subtitle="Enterprise QA architecture on one side, training the next generation of engineers on the other."
        />

        <div className="relative mt-16 md:mt-24">
          <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-ink/[0.08]" />
          <motion.div
            style={{ height: lineHeight }}
            className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 w-px timeline-line origin-top"
            aria-hidden="true"
          />

          <div className="relative flex flex-col gap-16 md:gap-20">
            <ColumnHeaders />

            {Array.from({
              length: Math.max(EXPERIENCE.length, TRAININGS.length),
            }).map((_, i) => (
              <Row key={i} index={i} left={EXPERIENCE[i]} right={TRAININGS[i]} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="max-w-3xl">
      <p className="section-eyebrow">{eyebrow}</p>
      <h2 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-5 text-ink/60 text-base md:text-lg max-w-xl leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function ColumnHeaders() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
      <div className="md:pr-10 md:text-right">
        <div className="inline-flex md:ml-auto items-center gap-3 border-b border-accent-violet/40 pb-3">
          <Briefcase size={15} className="text-accent-violet/80 md:order-2" />
          <p className="font-display text-lg md:text-xl font-normal tracking-tight text-ink/85">
            Professional Experience
          </p>
        </div>
      </div>
      <div className="md:pl-10">
        <div className="inline-flex items-center gap-3 border-b border-[#c4a35a]/40 pb-3">
          <GraduationCap size={15} className="text-[#c4a35a]/90" />
          <p className="font-display text-lg md:text-xl font-normal tracking-tight text-ink/85">
            Corporate Trainings Delivered
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ index, left, right }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-start">
      <div className="md:pr-10 md:text-right md:flex md:justify-end">
        {left ? (
          <MilestoneCard
            variant="left"
            icon={Briefcase}
            title={left.company}
            role={left.role}
            detail={left.detail}
            tags={left.tags}
            logo={left.logo}
            index={index}
          />
        ) : (
          <div />
        )}
      </div>
      <div className="md:pl-10">
        {right ? (
          <MilestoneCard
            variant="right"
            icon={GraduationCap}
            title={right.company}
            role={right.role}
            detail={right.detail || right.impact?.join(" · ")}
            tags={right.tags}
            logo={right.logo}
            iconTone={right.iconTone}
            country={right.country}
            index={index}
          />
        ) : (
          <div />
        )}
      </div>
    </div>
  );
}

const COUNTRY_META = {
  USA: { flag: "🇺🇸", label: "USA" },
  India: { flag: "🇮🇳", label: "India" },
};

function MilestoneCard({
  variant,
  icon: Icon,
  title,
  role,
  detail,
  tags,
  logo,
  iconTone,
  country,
  index,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const fromX = variant === "left" ? -40 : 40;
  const isLeft = variant === "left";
  const countryMeta = country ? COUNTRY_META[country] : null;

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, x: fromX, y: 20 }}
      animate={inView ? { opacity: 1, x: 0, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="group relative w-full max-w-md ml-12 md:ml-0"
      data-testid={`milestone-${variant}-${index}`}
    >
      <span
        className={`absolute top-6 -left-12 md:top-8 ${
          isLeft
            ? "md:-right-[calc(2.5rem+9px)] md:left-auto bg-accent-violet shadow-[0_0_18px_rgba(139,116,245,0.55)]"
            : "md:-left-[calc(2.5rem+9px)] bg-[#c4a35a] shadow-[0_0_16px_rgba(196,163,90,0.45)]"
        } h-3 w-3 rounded-full`}
        aria-hidden="true"
      />
      <div
        className={`relative overflow-hidden rounded-2xl p-6 md:p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 ${
          isLeft
            ? "border border-accent-violet/30 bg-gradient-to-br from-[#1a1430]/95 via-[#12101c]/90 to-[#0c0a12]/85 hover:border-accent-violet/50"
            : "border border-[#c4a35a]/25 bg-gradient-to-br from-[#1a1814]/90 via-[#141416]/90 to-[#101012]/85 hover:border-[#c4a35a]/45"
        }`}
      >
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 ${
            isLeft
              ? "bg-[radial-gradient(85%_65%_at_0%_0%,rgba(139,116,245,0.18),transparent_55%)]"
              : "bg-[radial-gradient(85%_65%_at_100%_0%,rgba(196,163,90,0.12),transparent_55%)]"
          }`}
        />
        <div
          className={`relative flex items-center gap-3 ${
            isLeft ? "md:justify-end" : ""
          }`}
        >
          {logo ? (
            <span className="inline-flex h-10 w-[4.5rem] md:h-11 md:w-24 items-center justify-center rounded-lg border border-ink/10 bg-white p-1.5 overflow-hidden shadow-sm">
              <img
                src={logo}
                alt={`${title} logo`}
                className="max-h-full max-w-full object-contain"
                loading="lazy"
              />
            </span>
          ) : iconTone === "yellow" || !isLeft ? (
            <span
              className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border ${
                isLeft
                  ? "border-yellow-300/40 bg-yellow-300/10 text-yellow-300 shadow-[0_0_18px_rgba(253,224,71,0.25)]"
                  : "border-[#c4a35a]/35 bg-[#c4a35a]/10 text-[#d4b56a]"
              }`}
            >
              <Icon size={16} />
            </span>
          ) : (
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-accent-violet/30 bg-accent-violet/10 text-accent-violet">
              <Icon size={16} />
            </span>
          )}
          <span
            className={`text-[11px] uppercase tracking-[0.28em] font-mono ${
              isLeft ? "text-accent-violet/70" : "text-[#c4a35a]/75"
            }`}
          >
            {isLeft ? "Enterprise" : "Training Partner"}
          </span>
          {countryMeta && (
            <span
              className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] shadow-[0_0_20px_rgba(34,211,238,0.35)] ${
                countryMeta.label === "USA"
                  ? "border border-accent-cyan/50 bg-accent-cyan/20 text-accent-cyan"
                  : "border border-emerald-400/50 bg-emerald-400/20 text-emerald-300"
              }`}
              aria-label={`Delivered in ${countryMeta.label}`}
            >
              <span className="text-sm leading-none" aria-hidden="true">
                {countryMeta.flag}
              </span>
              {countryMeta.label}
            </span>
          )}
        </div>
        <h3 className="relative mt-4 font-display text-2xl md:text-3xl tracking-tight">
          {title}
        </h3>
        <p
          className={`relative mt-1 text-sm ${
            isLeft ? "text-accent-violet/90" : "text-[#d4b56a]/90"
          }`}
        >
          {role}
        </p>
        {detail && (
          <p className="relative mt-3 text-ink/60 text-[15px] leading-relaxed">
            {detail}
          </p>
        )}
        {tags && tags.length > 0 && (
          <div
            className={`relative mt-4 flex flex-wrap gap-1.5 ${
              isLeft ? "md:justify-end" : ""
            }`}
          >
            {tags.map((t) => (
              <span
                key={t}
                className={`rounded-full px-2.5 py-1 text-[11px] ${
                  isLeft
                    ? "border border-accent-violet/25 bg-accent-violet/10 text-accent-violet/85"
                    : "border border-[#c4a35a]/25 bg-[#c4a35a]/10 text-[#d4b56a]/90"
                }`}
              >
                {t}
              </span>
            ))}
          </div>
        )}
        <div
          className={`relative mt-5 inline-flex items-center gap-1 text-xs ${
            isLeft ? "text-accent-violet/45 md:justify-self-end" : "text-[#c4a35a]/45"
          }`}
        >
          <ArrowUpRight size={12} />
          <span>Milestone · {String(index + 1).padStart(2, "0")}</span>
        </div>
      </div>
    </motion.article>
  );
}
